import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Check, X, Edit2, ArrowUp, ArrowDown, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HINT_HELP = {
  filler: "Например: пух-перо, лебяжий пух, 100% гусиный пух",
  material: "Например: 100% кашемир, кашемир-шерсть 50/50, 100% шерсть",
  fur: "Например: соболь, норка, шиншилла, чернобурая лиса",
};

function KindColumn({ kind, label, items, onReload }) {
  const [adding, setAdding] = useState("");
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const fail = (e) => setError(e?.response?.data?.detail || "Ошибка");

  const add = async () => {
    const value = adding.trim();
    if (!value) return;
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/ai-hints`, { kind, value }, { withCredentials: true });
      setAdding("");
      await onReload();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const save = async (id) => {
    const value = editValue.trim();
    if (!value) return;
    setBusy(true); setError("");
    try {
      await axios.put(`${API}/admin/ai-hints/${id}`, { value }, { withCredentials: true });
      setEditId(null);
      await onReload();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const remove = async (id, value) => {
    if (!window.confirm(`Удалить значение «${value}»?`)) return;
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/admin/ai-hints/${id}`, { withCredentials: true });
      await onReload();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/ai-hints/reorder`,
        { kind, ids: next.map((i) => i.id) }, { withCredentials: true });
      await onReload();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`ai-hint-col-${kind}`}
      style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
                padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontFamily: "Manrope", fontSize: 11, fontWeight: 700, color: "#6b7280",
                     letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}>
        {HINT_HELP[kind]}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 2 }}>
        {items.length === 0 && (
          <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#9ca3af" }}>
            Пока нет значений
          </div>
        )}
        {items.map((it, idx) => (
          <div key={it.id} data-testid={`ai-hint-item-${it.id}`}
            style={{ display: "flex", alignItems: "center", gap: 6,
                      background: "#FDFBF7", border: "1px solid #f3f4f6",
                      borderRadius: 6, padding: "6px 8px" }}>
            {editId === it.id ? (
              <>
                <input data-testid="ai-hint-edit-input" value={editValue} autoFocus
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") save(it.id); }}
                  style={{ flex: 1, border: "1px solid #C5A059", borderRadius: 4,
                            padding: "5px 7px", fontFamily: "Manrope", fontSize: 12.5,
                            outline: "none", minWidth: 0 }} />
                <button data-testid="ai-hint-save-btn" onClick={() => save(it.id)} disabled={busy}
                  style={{ background: "#0A0A0A", color: "#D4B878", border: "none",
                            borderRadius: 4, padding: "5px 7px", cursor: "pointer" }}>
                  <Check size={12} />
                </button>
                <button onClick={() => setEditId(null)}
                  style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4,
                            padding: "5px 7px", cursor: "pointer", color: "#6b7280" }}>
                  <X size={12} />
                </button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontFamily: "Manrope", fontSize: 12.5,
                                color: "#0A0A0A", minWidth: 0, overflowWrap: "anywhere" }}>
                  {it.value}
                </span>
                <button onClick={() => move(idx, -1)} disabled={busy || idx === 0}
                  title="Выше"
                  style={{ background: "none", border: "none", cursor: "pointer",
                            color: idx === 0 ? "#e5e7eb" : "#9ca3af", padding: 2 }}>
                  <ArrowUp size={12} />
                </button>
                <button onClick={() => move(idx, 1)} disabled={busy || idx === items.length - 1}
                  title="Ниже"
                  style={{ background: "none", border: "none", cursor: "pointer",
                            color: idx === items.length - 1 ? "#e5e7eb" : "#9ca3af", padding: 2 }}>
                  <ArrowDown size={12} />
                </button>
                <button data-testid={`ai-hint-edit-${it.id}`}
                  onClick={() => { setEditId(it.id); setEditValue(it.value); }}
                  style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4,
                            padding: "4px 6px", cursor: "pointer", color: "#374151" }}>
                  <Edit2 size={11} />
                </button>
                <button data-testid={`ai-hint-delete-${it.id}`}
                  onClick={() => remove(it.id, it.value)}
                  style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 4,
                            padding: "4px 6px", cursor: "pointer", color: "#dc2626" }}>
                  <Trash2 size={11} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <input data-testid={`ai-hint-new-${kind}`} value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="Новое значение…"
          style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px",
                    fontFamily: "Manrope", fontSize: 12.5, outline: "none", minWidth: 0 }} />
        <button data-testid={`ai-hint-add-${kind}`} onClick={add} disabled={busy || !adding.trim()}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                    border: "none", borderRadius: 6, padding: "8px 10px", cursor: "pointer",
                    opacity: adding.trim() ? 1 : 0.5, display: "flex", alignItems: "center" }}>
          <Plus size={13} />
        </button>
      </div>

      {error && (
        <div data-testid={`ai-hint-error-${kind}`}
          style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
                    borderRadius: 6, padding: "6px 8px", fontFamily: "Manrope", fontSize: 11.5 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default function AiHintsPanel() {
  const [kinds, setKinds] = useState([]);
  const [items, setItems] = useState({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/ai-hints`, { withCredentials: true });
      setKinds(r.data?.kinds || []);
      setItems(r.data?.items || {});
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="ai-hints-panel" style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Sparkles size={18} style={{ color: "#C5A059" }} />
        <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, margin: 0, fontWeight: 500 }}>
          Подсказки для ИИ: материалы и наполнители
        </h3>
      </div>
      <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280", marginBottom: 14,
                     lineHeight: 1.5, maxWidth: 720 }}>
        Эти значения появляются в карточке товара рядом с кнопкой «ИИ-описание».
        Менеджер выбирает нужное — и ИИ обязан использовать именно этот состав,
        а не выдумывать свой. Выбранное значение попадает в поле «Материал / состав» товара.
        Редактировать могут администратор и старший менеджер.
      </div>

      <div style={{ display: "grid", gap: 14,
                     gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {kinds.map((k) => (
          <KindColumn key={k.key} kind={k.key} label={k.label}
            items={items[k.key] || []} onReload={load} />
        ))}
      </div>
    </div>
  );
}
